import * as jwt from 'jsonwebtoken';
import fs from 'fs-extra';
import { logger } from './logger.js';
import { getLicenseFilePath, getSmartStackDir } from './license-paths.js';
import { getMachineIdentity, generateMachineFingerprint } from './device-id.js';
import {
  loadCache,
  saveCache,
  clearCache,
  isCacheValid,
  isHeartbeatDue,
  isInGracePeriod,
  isGraceExpired,
  isSigningKeyCacheValid,
  type LicenseCache,
  type LicenseFeatureScope,
} from './license-cache.js';
import {
  LicenseClient,
  getLicenseServerUrl,
  getApiKey,
  type ValidateResponse,
  type LicenseApiError,
} from './license-client.js';

// ── Legacy public key (RSA-2048, RS256) — used ONLY for old-format JWTs ────
const LEGACY_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA3Rgy76bx4SPse6vSnduR
OSv8YOk/8lGSK6/a+1cxyppsymCR8p/+yxzLl+phbCtm4qPhrp4K8BFVyyCw0uhG
xWyHzXvlbVfEO0YG5Ug5B3NVFr9/QaAHV1lVHgahxVFCglJZj7yVbFOY4uSpWNNg
bQj3H9lV50R5F7vmmB+N7kmgq1jVtaED3N6hnuybQoV/yJFdyf8KMMQgrndmh//l
csxveQKoRdG8wCb3BY4//8F0qLz21lqwPIM+cGZhUM1+ycYvC7HEke5xMxy6cCAS
6Xwvxno/Ml8D2sXTBLNMt8JVFwoAuDohz3xdpxjfJDvzgwSH0bCLF7TOzEGRJF+s
GwIDAQAB
-----END PUBLIC KEY-----`;

const LEGACY_ISSUER = 'atlashub.io';
const NEW_ISSUERS: [string, ...string[]] = ['license.atlashub.ch', 'license.smartstack.com'];
// Accept both legacy product IDs and new product tiers
const LEGACY_PRODUCT_IDS = ['smartstack-cli', 'smartstackcli'];
const NEW_PRODUCT_TIERS = ['standard', 'plus', 'premium'];
const COMPOSITE_PRODUCT_PREFIX = 'smartstack-cli-';

// ── Interfaces ──────────────────────────────────────────────────────────────

export interface LicenseInfo {
  companyId: string;
  clientName?: string;
  /** Product tier: legacy (starter/professional/enterprise) or new (standard/plus/premium) */
  edition: string;
  product?: string;
  jti?: string;
  environment?: string;
  /** Hierarchical features (new format) or flat legacy features */
  features: LicenseFeatureScope[];
  /** Legacy flat feature list (for backward compat display) */
  legacyFeatures?: string[];
  maxInstances?: number;
  maxUsers?: number;
  issuedAt: Date;
  expiresAt: Date;
  renewalDate?: string;
  daysRemaining: number;
  isValid: boolean;
  isExpired: boolean;
  isPerpetual?: boolean;
  gracePeriodDays?: number;
  bind?: { machineHash?: string; offline?: boolean };
}

export type ValidationSource = 'online' | 'cache' | 'offline' | 'legacy';

export interface LicenseValidationResult {
  valid: boolean;
  error?: string;
  license?: LicenseInfo;
  warnings: string[];
  source: ValidationSource;
}

// ── JWT format detection ────────────────────────────────────────────────────

type JwtFormat = 'legacy' | 'new';

/**
 * Detect JWT format by decoding the header (without verification).
 * Legacy: RS256 + issuer atlashub.io
 * New: RS512 + issuer license.atlashub.ch
 */
export function detectJwtFormat(token: string): JwtFormat {
  try {
    const headerB64 = token.split('.')[0];
    const header = JSON.parse(Buffer.from(headerB64, 'base64url').toString());
    if (header.alg === 'RS512') return 'new';
  } catch {
    // Fall through
  }
  return 'legacy';
}

// ── File operations ─────────────────────────────────────────────────────────

/**
 * Save license key to file.
 */
export async function saveLicenseKey(licenseKey: string): Promise<void> {
  const licensePath = getLicenseFilePath();
  await fs.ensureDir(getSmartStackDir());
  const writeOptions: fs.WriteFileOptions = { encoding: 'utf-8' };
  if (process.platform !== 'win32') {
    writeOptions.mode = 0o600;
  }
  await fs.writeFile(licensePath, licenseKey.trim(), writeOptions);
}

/**
 * Load license key from file.
 */
export async function loadLicenseKey(): Promise<string | null> {
  const licensePath = getLicenseFilePath();
  if (await fs.pathExists(licensePath)) {
    return (await fs.readFile(licensePath, 'utf-8')).trim();
  }
  return null;
}

/**
 * Remove license key file.
 */
export async function removeLicenseKey(): Promise<void> {
  const licensePath = getLicenseFilePath();
  if (await fs.pathExists(licensePath)) {
    await fs.remove(licensePath);
  }
}

// ── JWT validation helpers ──────────────────────────────────────────────────

/**
 * Normalize JWT features from either format:
 * - New: ft: [{ aid, app, modules: [{ mid, mod }] }]
 * - Legacy: features: ["feat1", "feat2"] (flat string array)
 */
function normalizeFeatures(decoded: jwt.JwtPayload): { features: LicenseFeatureScope[]; legacyFeatures?: string[] } {
  // New format: ft claim (hierarchical)
  if (decoded.ft && Array.isArray(decoded.ft)) {
    const features: LicenseFeatureScope[] = decoded.ft.map((scope: { aid?: string; app?: string; modules?: Array<{ mid?: string; mod?: string }> }) => ({
      applicationId: scope.aid || null,
      applicationCode: scope.app || '*',
      modules: (scope.modules || []).map((m: { mid?: string; mod?: string }) => ({
        moduleId: m.mid || '',
        moduleCode: m.mod || '',
      })),
    }));
    return { features };
  }

  // Legacy format: features claim (flat string array)
  if (decoded.features) {
    const raw = typeof decoded.features === 'string' ? JSON.parse(decoded.features) : decoded.features;
    if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'string') {
      // Convert flat features to a single wildcard scope for backward compat
      return {
        features: [{ applicationId: null, applicationCode: '*', modules: [] }],
        legacyFeatures: raw as string[],
      };
    }
  }

  // Default: wildcard
  return {
    features: [{ applicationId: null, applicationCode: '*', modules: [] }],
    legacyFeatures: ['*'],
  };
}

function extractLicenseInfo(decoded: jwt.JwtPayload): LicenseInfo {
  const now = new Date();
  const expiresAt = decoded.exp ? new Date(decoded.exp * 1000) : new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
  const issuedAt = decoded.iat ? new Date(decoded.iat * 1000) : now;
  const isPerpetual = (decoded.perp ?? decoded.perpetual) === true;
  const isExpired = !isPerpetual && expiresAt < now;
  const daysRemaining = isPerpetual
    ? Infinity
    : Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));

  const { features, legacyFeatures } = normalizeFeatures(decoded);

  // Bind: new format (bnd.mh/bnd.ofl) or legacy (bind.machine_hash/bind.offline)
  const bnd = decoded.bnd || decoded.bind;
  const bind = bnd ? {
    machineHash: bnd.mh ?? bnd.machine_hash,
    offline: bnd.ofl ?? bnd.offline,
  } : undefined;

  return {
    companyId: decoded.sub || 'unknown',
    clientName: decoded.cn ?? decoded.client_name,
    edition: decoded.prd ?? decoded.edition ?? 'standard',
    product: decoded.prd ?? decoded.product ?? decoded.aud as string,
    jti: decoded.jti,
    environment: decoded.env ?? decoded.environment,
    features,
    legacyFeatures,
    maxInstances: decoded.mi ?? decoded.max_instances,
    maxUsers: decoded.mu ?? decoded.max_users,
    issuedAt,
    expiresAt,
    renewalDate: decoded.rnd ?? decoded.renewal_date,
    daysRemaining,
    isValid: !isExpired,
    isExpired,
    isPerpetual,
    gracePeriodDays: decoded.gp ?? decoded.grace_period_days ?? 15,
    bind,
  };
}

/**
 * Validate a legacy JWT (RS256, issuer atlashub.io).
 */
export function validateLegacyLicenseKey(licenseKey: string): LicenseValidationResult {
  if (!licenseKey?.trim()) {
    return { valid: false, error: 'License key is empty', warnings: [], source: 'legacy' };
  }

  try {
    const decoded = jwt.verify(licenseKey, LEGACY_PUBLIC_KEY, {
      algorithms: ['RS256'],
      issuer: LEGACY_ISSUER,
    }) as jwt.JwtPayload;

    const productId = decoded.product_id || decoded.product || decoded.aud;
    if (!LEGACY_PRODUCT_IDS.includes(productId)) {
      return {
        valid: false,
        error: `Invalid license: This license is for "${productId}", not "smartstack-cli"`,
        warnings: [],
        source: 'legacy',
      };
    }

    const license = extractLicenseInfo(decoded);
    const warnings = [
      'This license uses the legacy format (RS256). It will continue to work but please upgrade to a new license for enhanced features.',
    ];

    if (license.isExpired) {
      return {
        valid: false,
        error: `License expired on ${license.expiresAt.toISOString().split('T')[0]}`,
        license,
        warnings,
        source: 'legacy',
      };
    }

    return { valid: true, license, warnings, source: 'legacy' };
  } catch (error) {
    if (error instanceof jwt.JsonWebTokenError) {
      return { valid: false, error: `Invalid license signature: ${error.message}`, warnings: [], source: 'legacy' };
    }
    if (error instanceof jwt.TokenExpiredError) {
      return { valid: false, error: 'License has expired', warnings: [], source: 'legacy' };
    }
    return { valid: false, error: `License validation failed: ${(error as Error).message}`, warnings: [], source: 'legacy' };
  }
}

/**
 * Validate a new-format JWT (RS512, issuer license.atlashub.ch).
 */
export function validateNewLicenseKey(licenseKey: string, signingKeyPem: string): LicenseValidationResult {
  if (!licenseKey?.trim()) {
    return { valid: false, error: 'License key is empty', warnings: [], source: 'offline' };
  }

  try {
    const decoded = jwt.verify(licenseKey, signingKeyPem, {
      algorithms: ['RS512'],
      issuer: NEW_ISSUERS,
    }) as jwt.JwtPayload;

    // New format: prd claim has product tier (standard/plus/premium)
    // Also accept legacy product IDs, transitional tokens, and composite IDs (smartstack-cli-standard)
    const productId = decoded.prd || decoded.product || decoded.aud;
    const allValid = [...NEW_PRODUCT_TIERS, ...LEGACY_PRODUCT_IDS];
    const isComposite = typeof productId === 'string' && productId.startsWith(COMPOSITE_PRODUCT_PREFIX);
    if (!allValid.includes(productId) && !isComposite) {
      return {
        valid: false,
        error: `Invalid license: unrecognized product "${productId}"`,
        warnings: [],
        source: 'offline',
      };
    }

    const license = extractLicenseInfo(decoded);

    if (license.isExpired && !license.isPerpetual) {
      const graceDays = license.gracePeriodDays ?? 15;
      if (isGraceExpired(license.expiresAt.toISOString(), graceDays)) {
        return {
          valid: false,
          error: `License expired on ${license.expiresAt.toISOString().split('T')[0]} (grace period ended)`,
          license,
          warnings: [],
          source: 'offline',
        };
      }

      const grace = isInGracePeriod(license.expiresAt.toISOString(), graceDays);
      if (grace.inGrace) {
        return {
          valid: true,
          license,
          warnings: [`License expired but within grace period (${grace.daysLeft} day(s) remaining)`],
          source: 'offline',
        };
      }
    }

    return { valid: true, license, warnings: [], source: 'offline' };
  } catch (error) {
    if (error instanceof jwt.JsonWebTokenError) {
      return { valid: false, error: `Invalid license signature: ${error.message}`, warnings: [], source: 'offline' };
    }
    if (error instanceof jwt.TokenExpiredError) {
      return { valid: false, error: 'License has expired', warnings: [], source: 'offline' };
    }
    return { valid: false, error: `License validation failed: ${(error as Error).message}`, warnings: [], source: 'offline' };
  }
}

/**
 * Validate an offline-bound JWT (RS512 + machine fingerprint match).
 */
export function validateOfflineLicense(
  licenseKey: string,
  signingKeyPem: string,
  fingerprint: string,
): LicenseValidationResult {
  const result = validateNewLicenseKey(licenseKey, signingKeyPem);
  if (!result.valid) return result;

  const bind = result.license?.bind;
  if (bind?.machineHash && bind.machineHash !== fingerprint) {
    return {
      valid: false,
      error: 'Machine fingerprint mismatch. This license is bound to a different machine.',
      warnings: [],
      source: 'offline',
    };
  }

  return result;
}

// ── Backward-compatible wrapper ─────────────────────────────────────────────

/**
 * Validate a license key — detects format automatically.
 * For legacy JWTs: offline RS256 validation.
 * For new JWTs: offline RS512 validation (requires signing key).
 */
export function validateLicenseKey(licenseKey: string): LicenseValidationResult {
  if (!licenseKey?.trim()) {
    return { valid: false, error: 'License key is empty', warnings: [], source: 'legacy' };
  }

  const format = detectJwtFormat(licenseKey);
  if (format === 'legacy') {
    return validateLegacyLicenseKey(licenseKey);
  }

  // New format — try to decode without verification for basic info
  // Full validation requires the signing key (online flow or cached key)
  try {
    const decoded = jwt.decode(licenseKey) as jwt.JwtPayload;
    if (!decoded) {
      return { valid: false, error: 'Invalid JWT token', warnings: [], source: 'offline' };
    }

    const productId = decoded.prd || decoded.product || decoded.aud;
    const allValid = [...NEW_PRODUCT_TIERS, ...LEGACY_PRODUCT_IDS];
    if (!allValid.includes(productId)) {
      return {
        valid: false,
        error: `Invalid license: unrecognized product "${productId}"`,
        warnings: [],
        source: 'offline',
      };
    }

    // Without signing key, we can only decode — mark as needing online validation
    const license = extractLicenseInfo(decoded);
    return {
      valid: true,
      license,
      warnings: ['License signature not verified (signing key unavailable). Run a command to trigger online validation.'],
      source: 'offline',
    };
  } catch {
    return { valid: false, error: 'Invalid JWT token', warnings: [], source: 'offline' };
  }
}

// ── Signing key management ──────────────────────────────────────────────────

async function getSigningKey(cache: LicenseCache, client: LicenseClient): Promise<string | null> {
  // Try cache first (24h TTL)
  if (isSigningKeyCacheValid(cache)) {
    return cache.signingKey!.pem;
  }

  // Fetch from server
  try {
    const pem = await client.fetchSigningKey();
    cache.signingKey = { pem, fetchedAt: new Date().toISOString() };
    await saveCache(cache);
    return pem;
  } catch {
    // Return cached key even if expired (better than nothing)
    return cache.signingKey?.pem || null;
  }
}

// ── Main validation flow ────────────────────────────────────────────────────

/**
 * Validate the current license — full online-first flow.
 *
 * Flow:
 * 1. Load JWT from ~/.smartstack/license.key
 * 2. Detect format (legacy vs new)
 * 3. Legacy → offline RS256 validation + deprecation warning
 * 4. New + bind.offline=true → offline RS512 + fingerprint match
 * 5. New + online:
 *    a. Cache valid (<60min) → use cached result (fast path)
 *    b. Try GET /machine/validate
 *       - OK → update cache, check heartbeat timing
 *       - Network fail → fallback to cache → fallback to offline JWT validation
 *    c. Apply grace period logic
 */
export async function validateCurrentLicense(): Promise<LicenseValidationResult> {
  const licenseKey = await loadLicenseKey();

  if (!licenseKey) {
    return {
      valid: false,
      error: 'No license found. Please activate with: smartstack license activate <key>',
      warnings: [],
      source: 'offline',
    };
  }

  const format = detectJwtFormat(licenseKey);

  // ── Legacy format → offline RS256 ──────────────────────────────────────
  if (format === 'legacy') {
    return validateLegacyLicenseKey(licenseKey);
  }

  // ── New format ─────────────────────────────────────────────────────────
  const cache = await loadCache();
  const baseUrl = await getLicenseServerUrl();
  const client = new LicenseClient(baseUrl);

  // Check for offline-bound license
  const decoded = jwt.decode(licenseKey) as jwt.JwtPayload | null;
  const bndClaim = decoded?.bnd || decoded?.bind;
  if (bndClaim?.ofl === true || bndClaim?.offline === true) {
    const signingKey = await getSigningKey(cache, client);
    if (!signingKey) {
      return {
        valid: false,
        error: 'Cannot validate offline license: signing key unavailable. Connect to the internet at least once.',
        warnings: [],
        source: 'offline',
      };
    }
    const fingerprint = generateMachineFingerprint();
    return validateOfflineLicense(licenseKey, signingKey, fingerprint);
  }

  // ── Fast path: cache still valid ───────────────────────────────────────
  if (isCacheValid(cache)) {
    const cached = cache.lastValidationResult!;
    const warnings: string[] = [];

    if (cached.revoked) {
      return { valid: false, error: 'License has been revoked', warnings: [], source: 'cache' };
    }

    if (!cached.isPerpetual && isGraceExpired(cached.expiresAt, cached.gracePeriodDays)) {
      return { valid: false, error: 'License expired (grace period ended)', warnings: [], source: 'cache' };
    }

    if (!cached.isPerpetual) {
      const grace = isInGracePeriod(cached.expiresAt, cached.gracePeriodDays);
      if (grace.inGrace) {
        warnings.push(`License expired but within grace period (${grace.daysLeft} day(s) remaining)`);
      }
    }

    // Lazy heartbeat check
    if (isHeartbeatDue(cache)) {
      await performHeartbeat(licenseKey, cache, client);
    }

    const license = buildLicenseInfoFromCache(cached, decoded);
    return { valid: true, license, warnings, source: 'cache' };
  }

  // ── Online validation ──────────────────────────────────────────────────
  try {
    const serverResult = await client.validate(licenseKey);
    return await handleOnlineValidation(serverResult, licenseKey, cache, client, decoded);
  } catch (err) {
    // Network failure → fallback to cache
    if (cache.lastValidationResult) {
      const cached = cache.lastValidationResult;
      const warnings = ['License server unreachable. Using cached validation result.'];

      if (cached.revoked) {
        return { valid: false, error: 'License has been revoked (cached)', warnings, source: 'cache' };
      }
      if (!cached.isPerpetual && isGraceExpired(cached.expiresAt, cached.gracePeriodDays)) {
        return { valid: false, error: 'License expired (cached, grace period ended)', warnings, source: 'cache' };
      }

      const license = buildLicenseInfoFromCache(cached, decoded);
      return { valid: true, license, warnings, source: 'cache' };
    }

    // No cache → offline JWT validation with signing key
    const signingKey = await getSigningKey(cache, client);
    if (signingKey) {
      const result = validateNewLicenseKey(licenseKey, signingKey);
      result.warnings.push('License server unreachable. Validated offline with cached signing key.');
      return result;
    }

    // Nothing works
    const apiErr = err as LicenseApiError;
    return {
      valid: false,
      error: apiErr?.message || 'Unable to validate license: server unreachable and no cached data.',
      warnings: [],
      source: 'offline',
    };
  }
}

async function handleOnlineValidation(
  serverResult: ValidateResponse,
  licenseKey: string,
  cache: LicenseCache,
  client: LicenseClient,
  decoded: jwt.JwtPayload | null,
): Promise<LicenseValidationResult> {
  const warnings: string[] = [];

  // Update cache — normalize features from server response format
  cache.lastValidation = new Date().toISOString();
  const normalizedFeatures: LicenseFeatureScope[] = Array.isArray(serverResult.features)
    ? serverResult.features.map(f => ({
        applicationId: f.application_id || null,
        applicationCode: f.application_code || '*',
        modules: (f.modules || []).map(m => ({
          moduleId: m.module_id || '',
          moduleCode: m.module_code || '',
        })),
      }))
    : [{ applicationId: null, applicationCode: '*', modules: [] }];

  cache.lastValidationResult = {
    valid: serverResult.valid,
    revoked: serverResult.revoked,
    environment: serverResult.environment,
    features: normalizedFeatures,
    maxInstances: serverResult.maxInstances,
    maxUsers: serverResult.maxUsers,
    expiresAt: serverResult.expiresAt,
    gracePeriodDays: serverResult.gracePeriodDays,
    isPerpetual: serverResult.isPerpetual,
    renewalDate: serverResult.renewalDate || null,
  };
  await saveCache(cache);

  if (serverResult.revoked) {
    return { valid: false, error: 'License has been revoked', warnings: [], source: 'online' };
  }

  if (!serverResult.valid && !serverResult.isPerpetual) {
    const graceDays = serverResult.gracePeriodDays ?? 15;
    if (isGraceExpired(serverResult.expiresAt, graceDays)) {
      return {
        valid: false,
        error: `License expired on ${serverResult.expiresAt.split('T')[0]} (grace period ended)`,
        warnings: [],
        source: 'online',
      };
    }

    const grace = isInGracePeriod(serverResult.expiresAt, graceDays);
    if (grace.inGrace) {
      warnings.push(`License expired but within grace period (${grace.daysLeft} day(s) remaining)`);
    }
  }

  // Lazy heartbeat
  if (isHeartbeatDue(cache)) {
    await performHeartbeat(licenseKey, cache, client);
  }

  const license = buildLicenseInfoFromValidateResponse(serverResult, decoded);
  return { valid: true, license, warnings, source: 'online' };
}

async function performHeartbeat(
  licenseKey: string,
  cache: LicenseCache,
  client: LicenseClient,
): Promise<void> {
  try {
    const identity = await getMachineIdentity();
    const apiKey = await getApiKey();

    let response;
    if (apiKey) {
      response = await client.heartbeatWithApiKey(apiKey, licenseKey, {
        machineFingerprint: identity.fingerprint,
        instanceId: identity.instanceId,
      });
    } else {
      response = await client.heartbeat(licenseKey, {
        machineFingerprint: identity.fingerprint,
        instanceId: identity.instanceId,
      });
    }

    cache.lastHeartbeat = new Date().toISOString();
    await saveCache(cache);

    // If the server returned a new JWT (license modified/superseded), update local file
    if (response.newJwtToken) {
      await saveLicenseKey(response.newJwtToken);
      logger.debug('License JWT updated from heartbeat response');
    }
  } catch {
    // Heartbeat failure is non-blocking — will retry next time
    logger.debug('Heartbeat failed (will retry later)');
  }
}

// ── Activation / Deactivation ───────────────────────────────────────────────

/**
 * Activate a license key on this machine.
 * Validates the JWT, registers with the license server, and saves cache.
 */
export async function activateLicense(licenseKey: string): Promise<LicenseValidationResult> {
  if (!licenseKey?.trim()) {
    return { valid: false, error: 'License key is empty', warnings: [], source: 'offline' };
  }

  const format = detectJwtFormat(licenseKey);

  // Legacy format — just save and validate offline
  if (format === 'legacy') {
    const result = validateLegacyLicenseKey(licenseKey);
    if (result.valid) {
      await saveLicenseKey(licenseKey);
    }
    return result;
  }

  // New format — validate + activate with server
  const baseUrl = await getLicenseServerUrl();
  const client = new LicenseClient(baseUrl);
  const cache = await loadCache();
  const identity = await getMachineIdentity();

  // Fetch signing key for local validation
  const signingKey = await getSigningKey(cache, client);

  // Try server-side activation
  try {
    const apiKey = await getApiKey();
    let activation;

    if (apiKey) {
      activation = await client.activateWithApiKey(apiKey, licenseKey, {
        machineFingerprint: identity.fingerprint,
        machineName: identity.machineName,
        instanceId: identity.instanceId,
      });
    } else {
      activation = await client.activate(licenseKey, {
        machineFingerprint: identity.fingerprint,
        machineName: identity.machineName,
        instanceId: identity.instanceId,
      });
    }

    // Save license key and activation cache
    await saveLicenseKey(licenseKey);
    cache.activation = {
      activated: activation.activated,
      activatedAt: activation.activatedAt || new Date().toISOString(),
      machineFingerprint: identity.fingerprint,
      instanceId: identity.instanceId,
      heartbeatIntervalHours: activation.heartbeatIntervalHours,
      gracePeriodDays: activation.gracePeriodDays,
    };
    cache.lastHeartbeat = new Date().toISOString();
    await saveCache(cache);

    // Return validated license info
    if (signingKey) {
      const result = validateNewLicenseKey(licenseKey, signingKey);
      result.source = 'online';
      return result;
    }

    // No signing key — decode without verification
    const decoded = jwt.decode(licenseKey) as jwt.JwtPayload;
    if (decoded) {
      return {
        valid: true,
        license: extractLicenseInfo(decoded),
        warnings: [],
        source: 'online',
      };
    }

    return { valid: true, warnings: ['Activated but could not decode license details'], source: 'online' };
  } catch (err) {
    const apiErr = err as LicenseApiError;
    let error = apiErr?.message || 'Activation failed';

    if (apiErr?.status === 409) {
      error = 'Maximum number of machine activations reached. Deactivate another machine first.';
    } else if (apiErr?.status === 410) {
      error = 'This license has been revoked or has expired.';
    } else if (apiErr?.status === 422) {
      error = 'This machine fingerprint is already linked to another active license for this client.';
    } else if (apiErr?.status === 404) {
      error = 'License not found on the server.';
    }

    return { valid: false, error, warnings: [], source: 'online' };
  }
}

/**
 * Deactivate the current license — notify server (best-effort) and clean up locally.
 */
export async function deactivateLicense(): Promise<void> {
  // Best-effort server notification (not critical if it fails)
  try {
    const cache = await loadCache();
    if (cache.activation) {
      // No dedicated deactivation endpoint in the API spec,
      // but removing local state is the primary goal.
      logger.debug('Clearing local license activation');
    }
  } catch {
    // Ignore
  }

  await removeLicenseKey();
  await clearCache();
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function buildLicenseInfoFromCache(
  cached: NonNullable<LicenseCache['lastValidationResult']>,
  decoded: jwt.JwtPayload | null,
): LicenseInfo {
  const expiresAt = new Date(cached.expiresAt);
  const now = new Date();
  const daysRemaining = cached.isPerpetual
    ? Infinity
    : Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));

  return {
    companyId: decoded?.sub || 'unknown',
    clientName: decoded?.cn ?? decoded?.client_name,
    edition: decoded?.prd ?? decoded?.edition ?? 'standard',
    product: decoded?.prd ?? decoded?.product,
    jti: decoded?.jti,
    environment: cached.environment,
    features: cached.features,
    maxInstances: cached.maxInstances,
    maxUsers: cached.maxUsers,
    issuedAt: decoded?.iat ? new Date(decoded.iat * 1000) : now,
    expiresAt,
    renewalDate: cached.renewalDate ?? undefined,
    daysRemaining,
    isValid: cached.valid && !cached.revoked,
    isExpired: !cached.isPerpetual && expiresAt < now,
    isPerpetual: cached.isPerpetual,
    gracePeriodDays: cached.gracePeriodDays,
  };
}

function buildLicenseInfoFromValidateResponse(
  response: ValidateResponse,
  decoded: jwt.JwtPayload | null,
): LicenseInfo {
  const expiresAt = new Date(response.expiresAt);
  const now = new Date();
  const daysRemaining = response.isPerpetual
    ? Infinity
    : Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));

  // Normalize features from server response format
  const features: LicenseFeatureScope[] = Array.isArray(response.features)
    ? response.features.map(f => ({
        applicationId: f.application_id || null,
        applicationCode: f.application_code || '*',
        modules: (f.modules || []).map(m => ({
          moduleId: m.module_id || '',
          moduleCode: m.module_code || '',
        })),
      }))
    : [{ applicationId: null, applicationCode: '*', modules: [] }];

  return {
    companyId: response.clientId,
    clientName: response.clientName,
    edition: response.product || decoded?.prd || 'standard',
    product: response.product,
    jti: decoded?.jti,
    environment: response.environment,
    features,
    maxInstances: response.maxInstances,
    maxUsers: response.maxUsers,
    issuedAt: decoded?.iat ? new Date(decoded.iat * 1000) : now,
    expiresAt,
    renewalDate: response.renewalDate ?? undefined,
    daysRemaining,
    isValid: response.valid && !response.revoked,
    isExpired: !response.isPerpetual && expiresAt < now,
    isPerpetual: response.isPerpetual,
    gracePeriodDays: response.gracePeriodDays,
  };
}

/**
 * Check if a feature (application or module code) is enabled in the license.
 * Supports hierarchical features: checks applicationCode and moduleCode.
 * Also checks legacyFeatures for backward compatibility.
 */
export function hasFeature(license: LicenseInfo, feature: string): boolean {
  // Check legacy flat features
  if (license.legacyFeatures) {
    if (license.legacyFeatures.includes('*') || license.legacyFeatures.includes(feature)) return true;
  }

  // Check hierarchical features
  for (const scope of license.features) {
    // Wildcard application = all features
    if (scope.applicationCode === '*') return true;
    // Match application code
    if (scope.applicationCode === feature) return true;
    // Match module code
    for (const mod of scope.modules) {
      if (mod.moduleCode === feature) return true;
    }
  }

  return false;
}

/**
 * Check license and exit if invalid (for CLI commands).
 */
export async function requireLicense(): Promise<LicenseInfo> {
  const result = await validateCurrentLicense();

  if (!result.valid) {
    logger.error(`License Error: ${result.error || 'Invalid license'}`);
    logger.info('\nTo activate a license:');
    logger.info('  smartstack license activate <your-license-key>');
    logger.info('\nTo purchase a license:');
    logger.info('  https://atlashub.io/products/smartstack-cli');
    process.exit(1);
  }

  // Display warnings (deprecation, grace period, etc.)
  for (const warning of result.warnings) {
    logger.warning(warning);
  }

  return result.license!;
}

/**
 * Get current license info (if valid).
 */
export async function getLicenseInfo(): Promise<LicenseInfo | null> {
  const result = await validateCurrentLicense();
  return result.valid ? result.license! : null;
}

/**
 * Format license info for display.
 */
export function formatLicenseInfo(license: LicenseInfo, source?: ValidationSource): string {
  const status = license.isExpired
    ? '🔴 EXPIRED'
    : license.daysRemaining <= 30
      ? `🟡 ${license.daysRemaining} days remaining`
      : license.isPerpetual
        ? '🟢 Perpetual'
        : `🟢 Valid (${license.daysRemaining} days)`;

  const edition = license.edition.toUpperCase();
  const env = license.environment ? ` (${license.environment})` : '';
  const sourceLabel = source ? ` [${source}]` : '';

  // Format features for display
  let featuresDisplay: string;
  if (license.legacyFeatures) {
    featuresDisplay = license.legacyFeatures.join(', ');
  } else {
    const parts: string[] = [];
    for (const scope of license.features) {
      if (scope.applicationCode === '*') {
        parts.push('All applications');
      } else if (scope.modules.length > 0) {
        const mods = scope.modules.map(m => m.moduleCode).join(', ');
        parts.push(`${scope.applicationCode} (${mods})`);
      } else {
        parts.push(scope.applicationCode);
      }
    }
    featuresDisplay = parts.join(', ') || 'N/A';
  }

  // Limits display
  const limitsLine = license.maxInstances || license.maxUsers
    ? `║  Limits:      ${(`${license.maxInstances ?? '∞'} instances, ${license.maxUsers ?? '∞'} users`).padEnd(45)}║\n`
    : '';

  return `
╔══════════════════════════════════════════════════════════════╗
║                    SMARTSTACK CLI LICENSE                     ║
╠══════════════════════════════════════════════════════════════╣
║  Status:      ${(status + sourceLabel).padEnd(45)}║
║  Company:     ${(license.clientName || license.companyId).padEnd(45)}║
║  Edition:     ${(edition + env).padEnd(45)}║
║  Expires:     ${(license.isPerpetual ? 'Never' : license.expiresAt.toISOString().split('T')[0]).padEnd(45)}║
╠══════════════════════════════════════════════════════════════╣
║  Features:    ${featuresDisplay.padEnd(45)}║
${limitsLine}╚══════════════════════════════════════════════════════════════╝
`;
}
